import { useContext, useEffect, useReducer, useState } from 'react';
import { useQuery } from '@apollo/client';
import * as R from 'ramda';

import WorkOrdersContext from '@atom/components/workOrders/WorkOrdersContext';
import { GET_WORK_ORDER } from '@atom/graph/work';
import { usePreviousValue } from '@atom/hooks/usePreviousValue';
import {
  INITIAL_WORK_VALIDATIONS,
  useWorkValidations,
} from '@atom/hooks/useWorkValidations';
import { Progress } from '@atom/mui';
import { TaskTemplate } from '@atom/types/taskTemplate';
import {
  WorkOrderAssetTreeType,
  WorkOrderDetailType,
  WorkOrderType,
} from '@atom/types/work';
import api from '@atom/utilities/api';
import { hasRolePermissions, ROLE_SETS } from '@atom/utilities/authUtilities';
import {
  TASK_TEMPLATES_ENDPOINT,
  WORK_ORDERS_ENDPOINT,
} from '@atom/utilities/endpoints';

import InfoView from './infoView/InfoView';
import MainView from './MainView';
import WorkOrderPreviewContext, {
  ExpandedInfo,
  reducer,
  View,
  WorkOrderActionTypes,
} from './WorkOrderPreviewContext';

import classes from './workOrderPreview.module.css';

const WorkOrderPreview = () => {
  const { input } = useContext(WorkOrdersContext);

  const { workValidations, setWorkValidations } = useWorkValidations();

  const [workOrderDetail, dispatch] = useReducer(
    reducer,
    {} as WorkOrderDetailType,
  );
  const [activeView, setActiveView] = useState<View>(View.MAIN);
  const [expandedInfo, setExpandedInfo] = useState<Set<ExpandedInfo>>(
    new Set([
      ExpandedInfo.LOCATION,
      ExpandedInfo.SUMMARY,
      ExpandedInfo.CUSTOM,
      ExpandedInfo.DETAIL,
    ]),
  );
  const [collapsedTasks, setCollapsedTasks] = useState<Set<string>>(
    new Set([]),
  );
  const [taskTemplates, setTaskTemplates] = useState<TaskTemplate[]>([]);
  const [loadingTaskTemplates, setLoadingTaskTemplates] =
    useState<boolean>(true);
  const [activeWorkOrderAsset, setActiveWorkOrderAsset] = useState<string>('');
  const [activeWorkAssetTaskId, setActiveWorkAssetTaskId] =
    useState<string>('');
  const [workOrderAsset, setWorkOrderAsset] = useState<WorkOrderAssetTreeType>(
    // @ts-ignore
    {},
  );
  const previousWorkOrderAsset = usePreviousValue(workOrderAsset);
  const [workOrderAssetLoading, setWorkOrderAssetLoading] =
    useState<boolean>(false);
  const [
    refetchWorkOrderAssetTreeLoading,
    setRefetchWorkOrderAssetTreeLoading,
  ] = useState<boolean>(false);

  const onCompleted = data => {
    const mappedWorkOrder = {
      ...data?.workOrder,
      type: R.pathOr(WorkOrderType.DEFAULT, ['workOrder', 'type'], data),
    };

    dispatch({
      type: WorkOrderActionTypes.SET_WORK_ORDER,
      data: mappedWorkOrder,
    });
  };

  const { loading, error, refetch } = useQuery(GET_WORK_ORDER, {
    variables: {
      id: input.workOrderId,
      showAdmin: hasRolePermissions(ROLE_SETS.ADMIN),
    },
    notifyOnNetworkStatusChange: true,
    fetchPolicy: 'no-cache',
    onCompleted,
  });

  const resetState = () => {
    setActiveView(View.MAIN);
    setExpandedInfo(
      new Set([
        ExpandedInfo.LOCATION,
        ExpandedInfo.SUMMARY,
        ExpandedInfo.CUSTOM,
        ExpandedInfo.DETAIL,
      ]),
    );
    setCollapsedTasks(new Set([]));
    setTaskTemplates([]);
    setLoadingTaskTemplates(true);
    setActiveWorkOrderAsset('');
    setActiveWorkAssetTaskId('');
    // @ts-ignore
    setWorkOrderAsset({});
    setWorkOrderAssetLoading(false);
    setRefetchWorkOrderAssetTreeLoading(false);
  };

  useEffect(() => {
    resetState();
    dispatch({
      type: WorkOrderActionTypes.SET_WORK_ORDER,
      // @ts-ignore
      data: {},
    });

    // If the current work order does not match the work order with validation
    // errors in global state, then clear it.
    if (input.workOrderId !== workValidations.workOrderId) {
      setWorkValidations(INITIAL_WORK_VALIDATIONS);
    }
  }, [input.workOrderId]);

  const getTaskTemplates = async (): Promise<TaskTemplate[]> => {
    const { data } = await api.get(TASK_TEMPLATES_ENDPOINT, {
      workOrderId: workOrderDetail.id,
    });

    setTaskTemplates(data);
    setLoadingTaskTemplates(false);
    return data;
  };

  useEffect(() => {
    if (workOrderDetail.id) {
      getTaskTemplates();
    }
  }, [workOrderDetail.id]);

  const refetchWorkOrderAssetTree = async () => {
    setRefetchWorkOrderAssetTreeLoading(true);
    const endpoint = `${WORK_ORDERS_ENDPOINT}/${workOrderDetail.id}/assets/${activeWorkOrderAsset}/tree`;
    const { data } = await api.get(endpoint);

    setRefetchWorkOrderAssetTreeLoading(false);
    return setWorkOrderAsset(data);
  };

  useEffect(() => {
    setWorkOrderAssetLoading(true);

    const getWorkOrderAssetTree = async () => {
      if (R.isEmpty(activeWorkOrderAsset)) {
        if (!R.isEmpty(previousWorkOrderAsset)) {
          refetch();
        }

        // @ts-ignore
        return setWorkOrderAsset({});
      }

      const endpoint = `${WORK_ORDERS_ENDPOINT}/${workOrderDetail.id}/assets/${activeWorkOrderAsset}/tree`;
      const { data } = await api.get(endpoint);

      return setWorkOrderAsset(data);
    };

    getWorkOrderAssetTree();
    setWorkOrderAssetLoading(false);
  }, [activeWorkOrderAsset, workOrderDetail.id]);

  const workOrderPreviewLoading = error || !workOrderDetail.id;

  const getContent = () => {
    const views = {
      [View.MAIN]: <MainView />,
      [View.INFO]: <InfoView />,
    };

    return views[activeView];
  };

  return (
    <WorkOrderPreviewContext.Provider
      value={{
        refetch,
        refetchWorkOrderAssetTree,
        refetchWorkOrderAssetTreeLoading,
        workOrderDetail,
        dispatch,
        loading,
        collapsedTasks,
        setCollapsedTasks,
        workOrderAssetLoading,
        workOrderAsset,
        setActiveWorkOrderAsset,
        activeWorkAssetTaskId,
        setActiveWorkAssetTaskId,
        activeView,
        setActiveView,
        expandedInfo,
        setExpandedInfo,
        taskTemplates,
        setTaskTemplates,
        loadingTaskTemplates,
        refetchTaskTemplates: getTaskTemplates,
      }}
    >
      <div className={classes.trayContainer}>
        {workOrderPreviewLoading ? (
          <div className={classes.loadingContainer}>
            <Progress />
          </div>
        ) : (
          getContent()
        )}
      </div>
    </WorkOrderPreviewContext.Provider>
  );
};

export default WorkOrderPreview;
